import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  // Session lives in a secure httpOnly cookie; restore it by asking the server who we are.
  useEffect(() => {
    api.me()
      .then((u) => setUser(u))
      .catch(() => setUser(null))
      .finally(() => setReady(true));
  }, []);

  const login = useCallback(async (username, password) => {
    const { user: u } = await api.login(username, password); // server sets the auth cookie
    setUser(u);
    return u;
  }, []);

  const register = useCallback(async (name, email, password) => {
    const { user: u } = await api.register(name, email, password, window.location.origin);
    setUser(u);
    return u;
  }, []);

  // Google OAuth: /auth/google/session already set the cookie; just adopt the user.
  const loginWithToken = useCallback((_token, u) => {
    setUser(u);
    return u;
  }, []);

  const logout = useCallback(async () => {
    try { await api.logout(); } catch { /* clear locally regardless */ }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, ready, login, register, loginWithToken, logout, isAuthed: !!user, isAdmin: user?.role === "admin" }),
    [user, ready, login, register, loginWithToken, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
